// src/Components/Modules/Billing/CreditDebitNotes.js
import React, { useState, useEffect } from 'react';
import { AxiosAuthInstance } from '../../../AxiosInterceptors';
import Breadcrumb from '../../Partials/Breadcrumb';
import {
    Box, Typography, Paper, Button, TextField, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Dialog, DialogTitle, DialogContent,
    DialogActions, Chip, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import { FaPlus } from 'react-icons/fa';
import Swal from 'sweetalert2';

export default function CreditDebitNotes() {
    const [notes, setNotes] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [form, setForm] = useState({ customer_id: '', type: 'credit', note_date: new Date().toISOString().split('T')[0], amount: '', reason: '' });

    const fetchData = async () => {
        const [nRes, cRes] = await Promise.all([
            AxiosAuthInstance.get('credit-debit-notes'),
            AxiosAuthInstance.get('add_customer') // Adjust if your customer endpoint is different
        ]);
        setNotes(nRes.data.notes || []);
        setCustomers(cRes.data.customers || cRes.data || []);
    };

    useEffect(() => { fetchData(); }, []);

    const handleSave = async () => {
        try {
            await AxiosAuthInstance.post('credit-debit-notes', form);
            Swal.fire({ icon: 'success', title: 'Note Created!', timer: 1500, showConfirmButton: false });
            setDialogOpen(false);
            setForm({ customer_id: '', type: 'credit', note_date: new Date().toISOString().split('T')[0], amount: '', reason: '' });
            fetchData();
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || 'Failed' });
        }
    };

    return (
        <Box sx={{ p: 3, bgcolor: '#f5f7fa', minHeight: '100vh' }}>
            <Breadcrumb title="Credit / Debit Notes" />
            <Paper sx={{ p: 3, mb: 3, display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h5" fontWeight={700}>Credit & Debit Notes</Typography>
                <Button variant="contained" startIcon={<FaPlus />} onClick={() => setDialogOpen(true)}>New Note</Button>
            </Paper>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                            <TableCell>Date</TableCell>
                            <TableCell>Customer</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Reason</TableCell>
                            <TableCell align="right">Amount</TableCell>
                            <TableCell>Status</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {notes.map((n) => (
                            <TableRow key={n.id} hover>
                                <TableCell>{n.note_date}</TableCell>
                                <TableCell>{n.customer?.name || '-'}</TableCell>
                                <TableCell>
                                    <Chip label={n.type === 'credit' ? 'Credit Note' : 'Debit Note'} 
                                        color={n.type === 'credit' ? 'success' : 'error'} size="small" />
                                </TableCell>
                                <TableCell>{n.reason || '-'}</TableCell>
                                <TableCell align="right">৳{Number(n.amount).toLocaleString()}</TableCell>
                                <TableCell><Chip label={n.status} size="small" color="primary" /></TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>New Credit / Debit Note</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Customer</InputLabel>
                            <Select value={form.customer_id} label="Customer" onChange={(e) => setForm({...form, customer_id: e.target.value})}>
                                <MenuItem value="">Select Customer</MenuItem>
                                {customers.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth size="small">
                            <InputLabel>Note Type</InputLabel>
                            <Select value={form.type} label="Note Type" onChange={(e) => setForm({...form, type: e.target.value})}>
                                <MenuItem value="credit">Credit Note (Reduce Balance)</MenuItem>
                                <MenuItem value="debit">Debit Note (Increase Balance)</MenuItem>
                            </Select>
                        </FormControl>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField label="Date" type="date" size="small" value={form.note_date} onChange={(e) => setForm({...form, note_date: e.target.value})} fullWidth InputLabelProps={{shrink: true}} />
                            <TextField label="Amount" type="number" size="small" value={form.amount} onChange={(e) => setForm({...form, amount: e.target.value})} fullWidth />
                        </Box>
                        <TextField label="Reason" multiline rows={2} value={form.reason} onChange={(e) => setForm({...form, reason: e.target.value})} fullWidth />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSave}>Save Note</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}